import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const TAB_TYPE_SLUGS = {
  contracts: "contracts",
  establishment_documents: "establishment",
  employee_documents: "employee",
  drivers_documents: "driver",
  vehicle_documents: "vehicle",
  business_docs: "branch",
  partners_documents: "owner",
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
  Lang: i18n.language,
});

const buildListUrl = (tab) => {
  if (tab === "all") return `${BASE_URL}/office-document-types/all`;
  if (tab === "deleted") return `${BASE_URL}/office-document-types/delete`;
  const slug = TAB_TYPE_SLUGS[tab];
  if (!slug) return null;
  return `${BASE_URL}/office-document-types/${slug}`;
};

const fetchPaginatedList = async ({ tab, page = 1, per_page = 20, search = "" }) => {
  const url = buildListUrl(tab);
  if (!url) throw new Error("Invalid tab");

  const qs = new URLSearchParams({
    page: String(page),
    per_page: String(per_page),
  });
  if (String(search).trim() !== "") {
    qs.set("search", String(search).trim());
  }

  const response = await axios.get(`${url}?${qs.toString()}`, {
    headers: authHeaders(),
  });
  return response.data.data;
};

export const fetchOfficeDocumentList = createAsyncThunk(
  "office_document_types/fetchList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const p = params != null && typeof params === "object" ? params : {};
      const data = await fetchPaginatedList({
        tab: p.tab ?? "all",
        page: p.page ?? 1,
        per_page: p.per_page ?? 20,
        search: p.search ?? "",
      });
      return { tab: p.tab ?? "all", data };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || "Failed to load data",
      });
    }
  }
);

export const fetchOfficeDocumentTypeNames = createAsyncThunk(
  "office_document_types/fetchNames",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchPaginatedList({ tab: "all", page: 1, per_page: 200 });
      return Array.isArray(data?.data) ? data.data : [];
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to load names",
      });
    }
  }
);

// Legacy exports — delegate to paginated fetch
export const fetchOfficeDocumentTypes = createAsyncThunk(
  "office_document_types/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const p = params != null && typeof params === "object" ? params : {};
      const data = await fetchPaginatedList({
        tab: "all",
        page: p.page ?? 1,
        per_page: p.per_page ?? 200,
        search: p.search ?? "",
      });
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to load data",
      });
    }
  }
);

export const fetchDocumentsByType = createAsyncThunk(
  "office_document_types/fetchByType",
  async (arg, { rejectWithValue }) => {
    try {
      const p =
        arg != null && typeof arg === "object"
          ? arg
          : { typeSlug: arg };
      const tab = Object.entries(TAB_TYPE_SLUGS).find(([, slug]) => slug === p.typeSlug)?.[0];
      if (!tab) {
        return rejectWithValue({ message: "Invalid type" });
      }
      const data = await fetchPaginatedList({
        tab,
        page: p.page ?? 1,
        per_page: p.per_page ?? 20,
        search: p.search ?? "",
      });
      return { typeSlug: p.typeSlug, data };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Fetch by type failed",
      });
    }
  }
);

export const fetchDeletedDoc = createAsyncThunk(
  "office_document_types/fetchDeleted",
  async (params = {}, { rejectWithValue }) => {
    try {
      const p = params != null && typeof params === "object" ? params : {};
      const data = await fetchPaginatedList({
        tab: "deleted",
        page: p.page ?? 1,
        per_page: p.per_page ?? 20,
      });
      return data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to load deleted data",
      });
    }
  }
);

export const addOfficeDoc = createAsyncThunk(
  "office_document_types/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/office-document-types/store`,
        userData,
        {
          headers: authHeaders(),
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Add failed",
      });
    }
  }
);

export const officeDocTypesRecord = createAsyncThunk(
  "office_document_types/officeDocTypesRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/office-document-types/show/${id}`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Record load failed",
      });
    }
  }
);

export const updateOfficeDoc = createAsyncThunk(
  "office_document_types/updateOfficeDoc",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/office-document-types/update`,
        { document_id: id, ...formData },
        {
          headers: authHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Update failed",
      });
    }
  }
);

export const restoreRecord = createAsyncThunk(
  "office_document_types/restoreRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/office-document-types/restore`,
        { document_id: id },
        {
          headers: authHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Update failed",
      });
    }
  }
);

export const deleteRecord = createAsyncThunk(
  "office_document_types/deleteRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/office-document-types/delete`,
        { document_id: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return { ...response.data, id };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Delete failed",
      });
    }
  }
);

const emptyList = () => ({ data: [], pagination: null });

const updatePaginationAfterDelete = (bucket) => {
  if (!bucket?.pagination) return;
  const perPage = Number(bucket.pagination.per_page) || 20;
  const total = Math.max(0, Number(bucket.pagination.total || 0) - 1);
  const last_page = Math.max(1, Math.ceil(total / perPage) || 1);
  bucket.pagination = { ...bucket.pagination, total, last_page };
};

// Slice
const OfficeDocumentTypeSlice = createSlice({
  name: "office_document_types",
  initialState: {
    all: emptyList(),
    byType: {},
    deleted: emptyList(),
    nameOptions: [],
    record: null,
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOfficeDocumentList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOfficeDocumentList.fulfilled, (state, action) => {
        state.isLoading = false;
        const { tab, data } = action.payload;
        if (tab === "all") {
          state.all = data;
        } else if (tab === "deleted") {
          state.deleted = data;
        } else {
          const slug = TAB_TYPE_SLUGS[tab];
          if (slug) state.byType[slug] = data;
        }
      })
      .addCase(fetchOfficeDocumentList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load data";
      })
      .addCase(fetchOfficeDocumentTypeNames.fulfilled, (state, action) => {
        state.nameOptions = action.payload;
      })
      .addCase(fetchOfficeDocumentTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOfficeDocumentTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.all = action.payload;
      })
      .addCase(fetchOfficeDocumentTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load data";
      })
      .addCase(fetchDocumentsByType.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDocumentsByType.fulfilled, (state, action) => {
        state.isLoading = false;
        const { typeSlug, data } = action.payload;
        state.byType[typeSlug] = data;
      })
      .addCase(fetchDocumentsByType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message;
      })
      .addCase(fetchDeletedDoc.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeletedDoc.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deleted = action.payload;
      })
      .addCase(fetchDeletedDoc.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message;
      })
      .addCase(addOfficeDoc.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addOfficeDoc.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addOfficeDoc.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      .addCase(officeDocTypesRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(officeDocTypesRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(officeDocTypesRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      .addCase(updateOfficeDoc.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOfficeDoc.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateOfficeDoc.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      .addCase(restoreRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(restoreRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(restoreRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      .addCase(deleteRecord.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedId = action.payload.id;

        if (Array.isArray(state.all?.data)) {
          state.all.data = state.all.data.filter((item) => item.id !== deletedId);
          updatePaginationAfterDelete(state.all);
        }

        Object.keys(state.byType).forEach((type) => {
          const bucket = state.byType[type];
          if (Array.isArray(bucket?.data)) {
            bucket.data = bucket.data.filter((item) => item.id !== deletedId);
            updatePaginationAfterDelete(bucket);
          }
        });

        state.nameOptions = state.nameOptions.filter((item) => item.id !== deletedId);
        state.success = action.payload.message;
      })
      .addCase(deleteRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      });
  },
});

export const { clearState } = OfficeDocumentTypeSlice.actions;
export default OfficeDocumentTypeSlice.reducer;
